import type { FormatShortDate } from 'cockpit/services/format/createFormatShortDate';
import { createFormatShortDate } from 'cockpit/services/format/createFormatShortDate';
import type { WorkSchedule } from 'v5/core/booking-billing/model';

/**
 * Date labels for resourcing UI (planned table, optimistic rows).
 * Day/month order comes from the injected {@link FormatShortDate} (active i18n locale when wired).
 */

const DEFAULT_FORMAT_SHORT_DATE = createFormatShortDate('en');

export function formatResourcingDisplayDate(
  dateStr: string | Date | undefined | null,
  formatShortDate: FormatShortDate = DEFAULT_FORMAT_SHORT_DATE
): string {
  return formatShortDate.format(dateStr);
}

/** Formats a simple calendar span (e.g. production window), not v1 booking events. */
export function formatResourcingCalendarDateRange(
  range: {
    start: string | Date | null | undefined;
    end: string | Date | null | undefined;
  },
  formatShortDate: FormatShortDate = DEFAULT_FORMAT_SHORT_DATE
): string {
  const start = formatResourcingDisplayDate(range.start, formatShortDate);
  const end = formatResourcingDisplayDate(range.end, formatShortDate);
  if (start && end && start !== end) {
    return `${start} → ${end}`;
  }
  return start || end || '';
}

/** Date labels from domain {@link WorkSchedule} list (booking assignments). */
export function formatResourcingScheduleDateRange(
  schedules: WorkSchedule[],
  formatShortDate: FormatShortDate = DEFAULT_FORMAT_SHORT_DATE
): string {
  if (schedules.length === 0) {
    return '';
  }
  return schedules
    .map(schedule => {
      if (schedule.kind === 'continuous') {
        const start = formatResourcingDisplayDate(
          schedule.range.from.toAbsoluteString().slice(0, 10),
          formatShortDate
        );
        const end = formatResourcingDisplayDate(
          schedule.range.to.toAbsoluteString().slice(0, 10),
          formatShortDate
        );
        if (start && end && start !== end) {
          return `${start} → ${end}`;
        }
        return start || end || '';
      }
      const start = formatResourcingDisplayDate(
        schedule.range.from.toString(),
        formatShortDate
      );
      const end = formatResourcingDisplayDate(
        schedule.range.to.toString(),
        formatShortDate
      );
      if (start && end && start !== end) {
        return `${start} → ${end}`;
      }
      return start || end || '';
    })
    .filter(Boolean)
    .join(', ');
}
